import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlchemyProvider } from '@m/alchemy-ui/AlchemyProvider';
import { NAMESPACE } from '@m/alchemy-ui/theme';
import { SlateRTE } from '@m/alchemy-ui/SlateRTE';
import { convertAttrs } from './common/convert-attrs';
import { type CrowdicityTextEditorProps } from './interfaces/editor';

interface TextEditor extends HTMLTextAreaElement {
  resetEditor?: () => void;
}

export const CrowdicityTextEditor = ({
  initialValue,
  nodeId,
  config,
  textAreaAttrs,
  stringMap
}: CrowdicityTextEditorProps) => {
  const [defaultValue, setDefaultValue] = useState(initialValue);
  const [value, setValue] = useState(initialValue);
  const textArea = useRef<TextEditor | null>(null);
  const [editorKey, setEditorKey] = useState(nodeId);

  const attrsToAssign = useMemo(() => {
    const { style, ...others } = textAreaAttrs || {};
    return convertAttrs(others);
  }, [textAreaAttrs]);

  const handleReset = useCallback(() => {
    setValue(initialValue);
    setDefaultValue(initialValue);
    setEditorKey(`${nodeId}-${Date.now()}`);
  }, [initialValue, nodeId]);

  useEffect(() => {
    if (textArea.current) {
      textArea.current.resetEditor = handleReset;
    }
  }, [handleReset]);

  // Handle input events to update defaultValue
  useEffect(() => {
    if (!textArea.current) {
      return;
    }

    const node = textArea.current;
    const handler = (e: Event) => {
      setDefaultValue((e?.target as HTMLTextAreaElement).value);
    };

    // Add input event listener for non-React context
    node.addEventListener('input', handler);
    return () => node.removeEventListener('input', handler);
  }, []);

  // Handle change events to notify external listeners
  useEffect(() => {
    if (!textArea.current) {
      return;
    }

    const node = textArea.current;
    const event = new Event('change', { bubbles: true });
    node.dispatchEvent(event);
  }, [value]);

  const charLimit = textAreaAttrs?.['data-gbcharacterlimit'];
  const hasCharLimit = Boolean(charLimit);
  const charsRemaining = hasCharLimit
    ? Number(charLimit) - value.trim().replace(/(<([^>]+)>)/gi, '').length
    : Number.NaN;
  const { buttons } = config;

  return (
    <AlchemyProvider namespace={NAMESPACE}>
      <div className={NAMESPACE}>
        <SlateRTE
          onChange={(val) => setValue(SlateRTE.serialize(val))}
          controls={buttons}
          initialValue={SlateRTE.deserialize(defaultValue)}
          key={editorKey}
        />
        {hasCharLimit && (
          <p className="small lightGrey">
            <strong style={charsRemaining < 0 ? { color: 'red' } : undefined}>{charsRemaining} </strong>
            {stringMap?.characterCount || 'characters remaining'}
          </p>
        )}
        <textarea {...attrsToAssign} ref={textArea} value={value} readOnly style={{ display: 'none' }} />
      </div>
    </AlchemyProvider>
  );
};
